import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getLocations } from '../services/locations.service';
import type { Database } from '../lib/database.types';
import 'react-quill/dist/quill.snow.css';
import EmailFormFields from '../components/EmailFormFields';
import {
  getEmailTextBlockByLocationAndType,
  getExistingEmailLanguages,
  createEmailTextBlock,
  updateEmailTextBlock,
  EmailTextBlock,
  EmailTextBlockInsert,
  EmailTextBlockUpdate
} from '../services/emailTextBlocks.service';
import { prepareHtmlForQuill, prepareHtmlForStorage } from '../utils/htmlConversion';

type Location = Database['public']['Tables']['locations']['Row'];

export default function EmailSettings() {
  const { merchantId, accessControl } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { defaultLanguage, availableLanguages } = useLanguage();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [activeTab, setActiveTab] = useState<1 | 2 | 3>(2);
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

  // Track which languages already have templates in the DB
  const [existingLanguages, setExistingLanguages] = useState<string[]>([]);

  // Email blocks for each type
  const [emailInitial, setEmailInitial] = useState<Partial<EmailTextBlock> | null>(null);
  const [emailCodes, setEmailCodes] = useState<Partial<EmailTextBlock> | null>(null);
  const [emailDelivery, setEmailDelivery] = useState<Partial<EmailTextBlock> | null>(null);

  // Form data for Tab 1 (Initial email - type 1)
  const [formDataInitial, setFormDataInitial] = useState<Partial<EmailTextBlock>>({
    email_type: 1,
    language: 'en',
    preheader_text: '',
    url: '',
    logo: '',
    intro: '',
    hi_hello: '',
    color: '#000000',
    header1: '',
    image: '',
    location_address: '',
    generic_block1: '',
    valid_from: '',
    valid_until: '',
    generic_block2: '',
    header2: '',
    generic_block3: '',
    generic_block4: '',
    header3: '',
    generic_block5: '',
    header4: '',
    generic_block6: '',
    extend_order_text: '',
    extend_order_app: '',
    extend_order_button: '',
    header5: '',
    generic_block7: '',
    take_picture_text: '',
    return_app: '',
    take_picture_button: '',
    generic_block8: '',
    cancel_booking_text: '',
    cancel_app: '',
    cancel_booking_button: '',
    download_receipt_text: '',
    download_terms_text: '',
    subject: '',
    location_name: ''
  });

  // Form data for Tab 2 (Email with codes - type 2)
  const [formDataCodes, setFormDataCodes] = useState<Partial<EmailTextBlock>>({
    email_type: 2,
    language: 'en',
    preheader_text: '',
    url: '',
    logo: '',
    intro: '',
    hi_hello: '',
    color: '#000000',
    header1: '',
    image: '',
    location_address: '',
    generic_block1: '',
    valid_from: '',
    valid_until: '',
    generic_block2: '',
    header2: '',
    generic_block3: '',
    generic_block4: '',
    header3: '',
    generic_block5: '',
    header4: '',
    generic_block6: '',
    extend_order_text: '',
    extend_order_app: '',
    extend_order_button: '',
    header5: '',
    generic_block7: '',
    take_picture_text: '',
    return_app: '',
    take_picture_button: '',
    generic_block8: '',
    cancel_booking_text: '',
    cancel_app: '',
    cancel_booking_button: '',
    download_receipt_text: '',
    download_terms_text: '',
    subject: '',
    location_name: ''
  });

  // Form data for Tab 3 (Email for deliveries - type 3)
  const [formDataDelivery, setFormDataDelivery] = useState<Partial<EmailTextBlock>>({
    email_type: 3,
    language: 'en',
    preheader_text: '',
    url: '',
    logo: '',
    intro: '',
    hi_hello: '',
    color: '#000000',
    header1: '',
    image: '',
    location_address: '',
    generic_block1: '',
    valid_from: '',
    valid_until: '',
    generic_block2: '',
    header2: '',
    generic_block3: '',
    generic_block4: '',
    header3: '',
    generic_block5: '',
    header4: '',
    generic_block6: '',
    extend_order_text: '',
    extend_order_app: '',
    extend_order_button: '',
    header5: '',
    generic_block7: '',
    take_picture_text: '',
    return_app: '',
    take_picture_button: '',
    generic_block8: '',
    cancel_booking_text: '',
    cancel_app: '',
    cancel_booking_button: '',
    download_receipt_text: '',
    download_terms_text: '',
    subject: '',
    location_name: ''
  });

  useEffect(() => {
    loadLocations();
  }, [merchantId]);

  useEffect(() => {
    if (selectedLocationId) {
      loadEmailBlock();
      loadExistingLanguages();
    }
  }, [selectedLocationId, selectedLanguage]);

  async function loadExistingLanguages() {
    if (!selectedLocationId) return;
    const { data } = await getExistingEmailLanguages(selectedLocationId);
    if (data) setExistingLanguages(data);
  }

  async function loadLocations() {
    if (!merchantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await getLocations(accessControl);

    if (error) {
      console.error('Error loading locations:', error);
    } else if (data) {
      setLocations(data);
      if (data.length > 0) {
        setSelectedLocationId(data[0].id);
        setSelectedLocation(data[0]);
      }
    }
    setLoading(false);
  }

  // Update selected location when location ID changes
  useEffect(() => {
    if (selectedLocationId && locations.length > 0) {
      const location = locations.find(l => l.id === selectedLocationId);
      setSelectedLocation(location || null);
    }
  }, [selectedLocationId, locations]);

  async function loadEmailBlock() {
    if (!selectedLocationId) return;

    setLoading(true);

    // Load all 3 email types for the selected language
    const [initialResult, codesResult, deliveryResult] = await Promise.all([
      getEmailTextBlockByLocationAndType(selectedLocationId, 1, selectedLanguage),
      getEmailTextBlockByLocationAndType(selectedLocationId, 2, selectedLanguage),
      getEmailTextBlockByLocationAndType(selectedLocationId, 3, selectedLanguage)
    ]);

    // Tab 1: Initial email (type 1)
    if (initialResult.data) {
      setEmailInitial(initialResult.data);
      setFormDataInitial({
        ...initialResult.data,
        location_address: prepareHtmlForQuill(initialResult.data.location_address),
        generic_block1: prepareHtmlForQuill(initialResult.data.generic_block1),
        generic_block2: prepareHtmlForQuill(initialResult.data.generic_block2),
        generic_block3: prepareHtmlForQuill(initialResult.data.generic_block3),
        generic_block4: prepareHtmlForQuill(initialResult.data.generic_block4),
        generic_block5: prepareHtmlForQuill(initialResult.data.generic_block5),
        generic_block6: prepareHtmlForQuill(initialResult.data.generic_block6),
        generic_block7: prepareHtmlForQuill(initialResult.data.generic_block7),
        generic_block8: prepareHtmlForQuill(initialResult.data.generic_block8),
        extend_order_text: prepareHtmlForQuill(initialResult.data.extend_order_text),
        take_picture_text: prepareHtmlForQuill(initialResult.data.take_picture_text),
        cancel_booking_text: prepareHtmlForQuill(initialResult.data.cancel_booking_text),
      });
    } else {
      setEmailInitial(null);
      setFormDataInitial({
        email_type: 1,
        language: selectedLanguage,
        location_id: selectedLocationId,
        merchant_id: merchantId,
        preheader_text: '',
        url: '',
        logo: '',
        intro: '',
        hi_hello: '',
        color: '#000000',
        header1: '',
        image: '',
        location_address: '',
        generic_block1: '',
        valid_from: '',
        valid_until: '',
        generic_block2: '',
        header2: '',
        generic_block3: '',
        generic_block4: '',
        header3: '',
        generic_block5: '',
        header4: '',
        generic_block6: '',
        extend_order_text: '',
        extend_order_app: '',
        extend_order_button: '',
        header5: '',
        generic_block7: '',
        take_picture_text: '',
        return_app: '',
        take_picture_button: '',
        generic_block8: '',
        cancel_booking_text: '',
        cancel_app: '',
        cancel_booking_button: '',
        download_receipt_text: '',
        download_terms_text: '',
        subject: '',
        location_name: ''
      });
    }

    // Tab 2: Email with codes (type 2)
    if (codesResult.data) {
      setEmailCodes(codesResult.data);
      setFormDataCodes({
        ...codesResult.data,
        location_address: prepareHtmlForQuill(codesResult.data.location_address),
        generic_block1: prepareHtmlForQuill(codesResult.data.generic_block1),
        generic_block2: prepareHtmlForQuill(codesResult.data.generic_block2),
        generic_block3: prepareHtmlForQuill(codesResult.data.generic_block3),
        generic_block4: prepareHtmlForQuill(codesResult.data.generic_block4),
        generic_block5: prepareHtmlForQuill(codesResult.data.generic_block5),
        generic_block6: prepareHtmlForQuill(codesResult.data.generic_block6),
        generic_block7: prepareHtmlForQuill(codesResult.data.generic_block7),
        generic_block8: prepareHtmlForQuill(codesResult.data.generic_block8),
        extend_order_text: prepareHtmlForQuill(codesResult.data.extend_order_text),
        take_picture_text: prepareHtmlForQuill(codesResult.data.take_picture_text),
        cancel_booking_text: prepareHtmlForQuill(codesResult.data.cancel_booking_text),
      });
    } else {
      setEmailCodes(null);
      setFormDataCodes({
        email_type: 2,
        language: selectedLanguage,
        location_id: selectedLocationId,
        merchant_id: merchantId,
        preheader_text: '',
        url: '',
        logo: '',
        intro: '',
        hi_hello: '',
        color: '#000000',
        header1: '',
        image: '',
        location_address: '',
        generic_block1: '',
        term_for_gate: '',
        valid_from: '',
        valid_until: '',
        generic_block2: '',
        header2: '',
        generic_block3: '',
        generic_block4: '',
        header3: '',
        generic_block5: '',
        header4: '',
        generic_block6: '',
        extend_order_text: '',
        extend_order_app: '',
        extend_order_button: '',
        header5: '',
        generic_block7: '',
        take_picture_text: '',
        return_app: '',
        take_picture_button: '',
        generic_block8: '',
        cancel_booking_text: '',
        cancel_app: '',
        cancel_booking_button: '',
        download_receipt_text: '',
        download_terms_text: '',
        subject: '',
        location_name: ''
      });
    }

    // Tab 3: Email for deliveries (type 3)
    if (deliveryResult.data) {
      setEmailDelivery(deliveryResult.data);
      setFormDataDelivery({
        ...deliveryResult.data,
        location_address: prepareHtmlForQuill(deliveryResult.data.location_address),
        generic_block1: prepareHtmlForQuill(deliveryResult.data.generic_block1),
        generic_block2: prepareHtmlForQuill(deliveryResult.data.generic_block2),
        generic_block3: prepareHtmlForQuill(deliveryResult.data.generic_block3),
        generic_block4: prepareHtmlForQuill(deliveryResult.data.generic_block4),
        generic_block5: prepareHtmlForQuill(deliveryResult.data.generic_block5),
        generic_block6: prepareHtmlForQuill(deliveryResult.data.generic_block6),
        generic_block7: prepareHtmlForQuill(deliveryResult.data.generic_block7),
        generic_block8: prepareHtmlForQuill(deliveryResult.data.generic_block8),
        extend_order_text: prepareHtmlForQuill(deliveryResult.data.extend_order_text),
        take_picture_text: prepareHtmlForQuill(deliveryResult.data.take_picture_text),
        cancel_booking_text: prepareHtmlForQuill(deliveryResult.data.cancel_booking_text),
      });
    } else {
      setEmailDelivery(null);
      setFormDataDelivery({
        email_type: 3,
        language: selectedLanguage,
        location_id: selectedLocationId,
        merchant_id: merchantId,
        preheader_text: '',
        url: '',
        logo: '',
        intro: '',
        hi_hello: '',
        color: '#000000',
        header1: '',
        image: '',
        location_address: '',
        generic_block1: '',
        valid_from: '',
        valid_until: '',
        generic_block2: '',
        header2: '',
        generic_block3: '',
        generic_block4: '',
        header3: '',
        generic_block5: '',
        header4: '',
        generic_block6: '',
        extend_order_text: '',
        extend_order_app: '',
        extend_order_button: '',
        header5: '',
        generic_block7: '',
        take_picture_text: '',
        return_app: '',
        take_picture_button: '',
        generic_block8: '',
        cancel_booking_text: '',
        cancel_app: '',
        cancel_booking_button: '',
        download_receipt_text: '',
        download_terms_text: '',
        subject: '',
        location_name: ''
      });
    }

    setLoading(false);
  }

  // Helper function to convert HTML fields for saving
  const prepareDataForSave = (formData: Partial<EmailTextBlock>) => ({
    ...formData,
    location_address: prepareHtmlForStorage(formData.location_address),
    generic_block1: prepareHtmlForStorage(formData.generic_block1),
    generic_block2: prepareHtmlForStorage(formData.generic_block2),
    generic_block3: prepareHtmlForStorage(formData.generic_block3),
    generic_block4: prepareHtmlForStorage(formData.generic_block4),
    generic_block5: prepareHtmlForStorage(formData.generic_block5),
    generic_block6: prepareHtmlForStorage(formData.generic_block6),
    generic_block7: prepareHtmlForStorage(formData.generic_block7),
    generic_block8: prepareHtmlForStorage(formData.generic_block8),
    extend_order_text: prepareHtmlForStorage(formData.extend_order_text),
    take_picture_text: prepareHtmlForStorage(formData.take_picture_text),
    cancel_booking_text: prepareHtmlForStorage(formData.cancel_booking_text),
  });

  // Helper function to convert data back for editing
  const prepareDataForEdit = (data: EmailTextBlock) => ({
    ...data,
    location_address: prepareHtmlForQuill(data.location_address),
    generic_block1: prepareHtmlForQuill(data.generic_block1),
    generic_block2: prepareHtmlForQuill(data.generic_block2),
    generic_block3: prepareHtmlForQuill(data.generic_block3),
    generic_block4: prepareHtmlForQuill(data.generic_block4),
    generic_block5: prepareHtmlForQuill(data.generic_block5),
    generic_block6: prepareHtmlForQuill(data.generic_block6),
    generic_block7: prepareHtmlForQuill(data.generic_block7),
    generic_block8: prepareHtmlForQuill(data.generic_block8),
    extend_order_text: prepareHtmlForQuill(data.extend_order_text),
    take_picture_text: prepareHtmlForQuill(data.take_picture_text),
    cancel_booking_text: prepareHtmlForQuill(data.cancel_booking_text),
  });

  async function handleSave() {
    if (!selectedLocationId || !merchantId) return;

    setSaving(true);

    // Determine which data to save based on active tab
    let formData: Partial<EmailTextBlock>;
    let emailBlock: Partial<EmailTextBlock> | null;
    let emailType: number;
    let setEmailBlock: (block: Partial<EmailTextBlock> | null) => void;
    let setFormData: (data: Partial<EmailTextBlock>) => void;

    if (activeTab === 1) {
      formData = formDataInitial;
      emailBlock = emailInitial;
      emailType = 1;
      setEmailBlock = setEmailInitial;
      setFormData = setFormDataInitial;
    } else if (activeTab === 2) {
      formData = formDataCodes;
      emailBlock = emailCodes;
      emailType = 2;
      setEmailBlock = setEmailCodes;
      setFormData = setFormDataCodes;
    } else {
      formData = formDataDelivery;
      emailBlock = emailDelivery;
      emailType = 3;
      setEmailBlock = setEmailDelivery;
      setFormData = setFormDataDelivery;
    }

    // Convert HTML fields from Quill format to handlebars format before saving
    const dataToSave: EmailTextBlockInsert | EmailTextBlockUpdate = {
      ...prepareDataForSave(formData),
      location_id: selectedLocationId,
      merchant_id: merchantId,
      email_type: emailType,
      language: selectedLanguage
    };

    let result;
    if (emailBlock?.id) {
      result = await updateEmailTextBlock(emailBlock.id, dataToSave);
    } else {
      result = await createEmailTextBlock(dataToSave as EmailTextBlockInsert);
    }

    if (result.error) {
      showToast(t('emailSettings.errorSaving') + result.error, 'error');
    } else {
      showToast(t('emailSettings.savedSuccess'), 'success');
      if (result.data) {
        setEmailBlock(result.data);
        setFormData(prepareDataForEdit(result.data));
      }
      // Refresh existing languages after creating/updating a template
      loadExistingLanguages();
    }
    setSaving(false);
  }

  const updateFieldInitial = (field: keyof EmailTextBlock, value: any) => {
    setFormDataInitial(prev => ({ ...prev, [field]: value }));
  };

  const updateFieldCodes = (field: keyof EmailTextBlock, value: any) => {
    setFormDataCodes(prev => ({ ...prev, [field]: value }));
  };

  const updateFieldDelivery = (field: keyof EmailTextBlock, value: any) => {
    setFormDataDelivery(prev => ({ ...prev, [field]: value }));
  };

  // Determine tab visibility based on location properties
  const showTab1 = selectedLocation && !selectedLocation.deliveries &&
    (!selectedLocation.self_service_enabled || selectedLocation.two_emails_enabled);
  const showTab2 = selectedLocation && !selectedLocation.deliveries && selectedLocation.self_service_enabled;
  const showTab3 = selectedLocation && selectedLocation.deliveries;

  // Keep the active tab on one that is visible for the selected location
  useEffect(() => {
    if (!selectedLocation) return;
    const visibleTabs = ([1, 2, 3] as const).filter(tab =>
      tab === 1 ? showTab1 : tab === 2 ? showTab2 : showTab3
    );
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [selectedLocation]);

  if (loading && locations.length === 0) {
    return (
      <div className="container-centered">
        <p>{t('emailSettings.loadingLocations')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      <div className="flex items-center gap-md mb-xl">
        <button onClick={() => navigate('/dashboard')} className="btn btn-ghost p-xs">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="heading-page">{t('emailSettings.title')}</h1>
          <p className="text-sm text-secondary">{t('emailSettings.subtitle')}</p>
        </div>
      </div>

      <div className="space-y-lg">
        {/* Location Selector */}
        <div className="card mb-lg">
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">{t('emailSettings.selectLocation')}</label>
              <select
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="input w-full max-w-md mt-sm"
              >
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.location_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Language Selector */}
        {availableLanguages.length > 0 && (
          <div className="card mb-lg">
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">{t('emailSettings.emailLanguage')}</label>
                <p className="text-sm text-secondary mb-sm">
                  {t('emailSettings.languageDesc')}
                </p>
                <div className="flex gap-sm mt-sm overflow-x-auto">
                  {availableLanguages.map((lang) => {
                    const hasTemplate = existingLanguages.includes(lang.code);
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setSelectedLanguage(lang.code)}
                        className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                          selectedLanguage === lang.code
                            ? 'bg-blue text-white'
                            : 'bg-secondary text-secondary hover:bg-primary/10'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                        {lang.code === defaultLanguage && (
                          <span className="text-xs opacity-75 ml-xs">{t('common.default')}</span>
                        )}
                        {!hasTemplate && (
                          <span className={`text-xs px-xs py-px rounded ${
                            selectedLanguage === lang.code
                              ? 'bg-white/20 text-white'
                              : 'bg-warning/20 text-warning'
                          }`}>{t('common.new')}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        {selectedLocationId && (showTab1 || showTab2 || showTab3) && (
          <div className="card mb-lg">
            <div className="card-body">
              <div className="flex gap-sm">
                {showTab1 && (
                  <button
                    onClick={() => setActiveTab(1)}
                    className={`btn ${activeTab === 1 ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    {t('emailSettings.initialEmail')}
                  </button>
                )}
                {showTab2 && (
                  <button
                    onClick={() => setActiveTab(2)}
                    className={`btn ${activeTab === 2 ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    {t('emailSettings.emailWithCodes')}
                  </button>
                )}
                {showTab3 && (
                  <button
                    onClick={() => setActiveTab(3)}
                    className={`btn ${activeTab === 3 ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    {t('emailSettings.emailForDeliveries')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab 1: Initial Email */}
        {selectedLocationId && showTab1 && activeTab === 1 && (
          <div className="card">
            <div className="card-header">
              <h2 className="heading-section">{t('emailSettings.initialEmail')}</h2>
            </div>
            <div className="card-body">
              <EmailFormFields
                formData={formDataInitial}
                updateField={updateFieldInitial}
                emailType={1}
              />

              {/* Save Button */}
              <div className="mt-lg flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary"
                >
                  <Save className="w-4 h-4" />
                  {saving ? t('common.saving') : t('emailSettings.saveEmailSettings')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Email with Codes */}
        {selectedLocationId && showTab2 && activeTab === 2 && (
          <div className="card">
            <div className="card-header">
              <h2 className="heading-section">{t('emailSettings.emailWithCodes')}</h2>
            </div>
            <div className="card-body">
              <EmailFormFields
                formData={formDataCodes}
                updateField={updateFieldCodes}
                emailType={2}
              />

              {/* Save Button */}
              <div className="mt-lg flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary"
                >
                  <Save className="w-4 h-4" />
                  {saving ? t('common.saving') : t('emailSettings.saveEmailSettings')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: Email for Deliveries */}
        {selectedLocationId && showTab3 && activeTab === 3 && (
          <div className="card">
            <div className="card-header">
              <h2 className="heading-section">{t('emailSettings.emailForDeliveries')}</h2>
            </div>
            <div className="card-body">
              <EmailFormFields
                formData={formDataDelivery}
                updateField={updateFieldDelivery}
                emailType={3}
              />

              {/* Save Button */}
              <div className="mt-lg flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary"
                >
                  <Save className="w-4 h-4" />
                  {saving ? t('common.saving') : t('emailSettings.saveEmailSettings')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
